"use client";

import cn from "@utils/cn";
import { toUnicodeLocale } from "@utils/string";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import React, { FC, useContext, useEffect, useState } from "react";
import FocusLock from "react-focus-lock";

import AvailableUnitsList from "@/components/AvailableUnitsList/AvailableUnitsList";
import HeaderDesktop from "@/components/Header/HeaderDesktop";
import HeaderMobile from "@/components/Header/HeaderMobile";
import HeaderSettings from "@/components/HeaderSettings/HeaderSettings";
import Icon from "@/components/ui/Icon/Icon";
import { BannerContext } from "@/context/BannerContext";
import { NavigationContext } from "@/context/NavigationContext";
import { SelectUnitContext } from "@/context/SelectUnitContext";
import { TemplateContext } from "@/context/TemplateContext";
import { UserContext } from "@/context/UserContext";
import { HeaderProps, Site, TargetSite } from "@/types";

import HeaderDesktopCorporate from "../HeaderCorp/HeaderDesktopCorporate";
import HeaderMobileCorporate from "../HeaderCorp/HeaderMobileCorporate";
import LanguageSettings from "../HeaderSettings/LanguageSettings";
import HeaderCheckout from "./HeaderCheckout";

const Header: FC<HeaderProps> = (props) => {
  const t = useTranslations();
  const { loginError } = useContext(UserContext);
  const { loginSuccess } = useContext(UserContext);

  const { successMessage, errorMessage } = useContext(BannerContext);

  const panelVariation = {
    open: { opacity: 1, display: "block" },
    close: { opacity: 0, transitionEnd: { display: "none" } },
  };
  const transition = {
    duration: 0.3,
  };
  const { desktopPanelID, setDesktopPanelID } = useContext(NavigationContext);
  const [trigger, setTrigger] = useState<HTMLElement>();
  const { card } = useContext(SelectUnitContext);
  const locale = toUnicodeLocale(useLocale());
  const { visibleHeader } = useContext(TemplateContext);

  return props ? (
    <>
      {props?.minimal ? (
        <>
          <HeaderCheckout {...props} />
        </>
      ) : (
        <>
          {loginSuccess && (
            <div
              className={`${visibleHeader ? "top-[var(--header-height)]" : "top-[0px]"} md:top-[var(--header-height)] sticky rounded w-full h-xxl z-[100] bg-[#f0fbf4] flex justify-center items-center text-center`}
            >
              <span className="text-[#278a45] font-small">{t("login.banner.label.success")}</span>
            </div>
          )}
          {loginError && (
            <div
              className={`${visibleHeader ? "top-[var(--header-height)]" : "top-[0px]"} md:top-[var(--header-height)] sticky bg-[#F8F7F7] z-[3000] w-full rounded text-center`}
            >
              <div
                className="bg-status-error/30 text-status-error rounded w-full p-s text-center"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
              >
                <span className="font-small">{t("label.login.credential.error")}</span>
              </div>
            </div>
          )}
          {successMessage?.length > 0 && (
            <div
              className={`${visibleHeader ? "top-[var(--header-height)]" : "top-[0px]"} md:top-[var(--header-height)] sticky rounded w-full h-xxl z-[100] bg-[#f0fbf4] flex justify-center items-center text-center`}
            >
              <span className="text-[#278a45] font-small">{successMessage}</span>
            </div>
          )}
          {errorMessage?.length > 0 && (
            <div
              className={`${visibleHeader ? "top-[var(--header-height)]" : "top-[0px]"} md:top-[var(--header-height)] sticky bg-[#F8F7F7] z-[3000] w-full rounded text-center`}
            >
              <div
                className="bg-status-error/30 text-status-error rounded w-full p-s text-center"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
              >
                <span className="font-small">{errorMessage}</span>
              </div>
            </div>
          )}
          {props?.target === Site.damacDigital ? (
            <>
              <HeaderMobileCorporate {...props} />
              <HeaderDesktopCorporate {...props} />
            </>
          ) : (
            <>
              <HeaderDesktop {...props} />
              <HeaderMobile {...props} />
              <motion.div
                id="modal-panel-select-unit"
                initial="close"
                variants={panelVariation}
                animate={desktopPanelID === "select-unit" ? "open" : "close"}
                transition={transition}
                key="subpanel-select-unit"
                className="pt-m fixed left-0 top-0 bg-white w-full h-screen overflow-auto z-50"
              >
                <FocusLock
                  returnFocus={(el) => {
                    if (!trigger) {
                      setTrigger(el as HTMLElement);
                    } else {
                      trigger.focus();
                      setTrigger(undefined);
                    }
                    return true;
                  }}
                  disabled={desktopPanelID !== "select-unit"}
                >
                  {card && (
                    <>
                      <div className="container">
                        <div className="text-right invert">
                          <button
                            onClick={() => {
                              setDesktopPanelID("");
                            }}
                          >
                            <Icon name="close" size="32" className="stroke-black" />
                          </button>
                        </div>
                        <div className="md:mt-4xl">
                          {card?.unitType && card?.categoryID && (
                            <AvailableUnitsList propertyType={card?.unitType} category={card?.categoryID} locale={locale} />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </FocusLock>
              </motion.div>
            </>
          )}
          <HeaderSettings settings={props?.settings?.settings ?? {}} />
          <LanguageSettings settings={props?.settings?.settings ?? {}} />
        </>
      )}
    </>
  ) : (
    <></>
  );
};
export default Header;
